(() => {
  const desktop = window.matchMedia('(min-width: 901px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const navKeys = ['about', 'shapewear', 'home', 'lookbook', 'photobooth'];
  const rootHashes = new Set(['', '#about', '#shop', '#shapewear', '#photobooth', '#lookbook']);
  const shell = document.querySelector('[data-angel-route-shell]');
  const main = document.querySelector('#MainContent');
  if (!shell || !main) return;

  const canEnhance = () => desktop.matches && !reducedMotion.matches && !window.Shopify?.designMode;
  const cache = new Map();
  let current = {
    key: location.pathname + location.search + location.hash,
    title: document.title,
    nodes: [...main.childNodes],
    // These parser-created scripts ran during the initial document load.
    scriptsActivated: true,
  };
  let activeController;

  const isInternal = (url) => url.origin === location.origin;
  const isEligible = (url) => {
    if (!isInternal(url)) return false;
    if (url.pathname === '/' || url.pathname === '') return rootHashes.has(url.hash);
    return url.pathname.startsWith('/products/');
  };
  const targetLayout = (url) => url.pathname.startsWith('/products/') ? 'compact' : 'expanded';
  const keyFor = (url) => url.pathname + url.search + url.hash;
  const slots = () => navKeys.map((key) => shell.querySelector(`[data-angel-nav-item="${key}"]`)).filter(Boolean);

  // Route changes must never inherit the Lookbook viewer's document-wide lock.
  // This runs before an enhanced navigation starts, so a quick close-then-link
  // tap cannot leave the global navigation hidden during the next route.
  const releaseLookbookViewer = () => {
    document.querySelectorAll('[data-angel-lookbook-lightbox]').forEach((lightbox) => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
    });
    document.body.classList.remove('angel-lookbook-lightbox-open');
    document.documentElement.classList.remove('angel-lookbook-lightbox-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
  };

  const applyCartCount = (cart) => {
    const count = shell.querySelector('[data-angel-shell-cart-count]');
    if (!count || !cart) return;
    count.textContent = cart.item_count;
    count.hidden = cart.item_count === 0;
    shell.querySelector('[data-angel-shell-cart]')?.setAttribute('aria-label', `Cart${cart.item_count ? `, ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}` : ''}`);
  };

  const setCartCount = async () => {
    if (!window.routes?.cart_url) return;
    try {
      const response = await fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      applyCartCount(await response.json());
    } catch (_) {}
  };

  const activateScripts = (root) => {
    root.querySelectorAll('script:not([src])').forEach((script) => {
      const type = script.getAttribute('type');
      if (type && !['text/javascript', 'application/javascript', 'module'].includes(type)) return;
      const next = document.createElement('script');
      if (type) next.type = type;
      next.textContent = script.textContent;
      script.replaceWith(next);
    });
  };

  const fetchPage = async (url) => {
    const key = keyFor(url);
    if (cache.has(key)) return cache.get(key);
    activeController?.abort();
    activeController = new AbortController();
    const response = await fetch(url.href, { credentials: 'same-origin', signal: activeController.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Route response ${response.status}`);
    const html = await response.text();
    const documentResponse = new DOMParser().parseFromString(html, 'text/html');
    const incomingMain = documentResponse.querySelector('#MainContent');
    if (!incomingMain) throw new Error('Missing route content');
    const page = {
      key,
      title: documentResponse.title,
      description: documentResponse.querySelector('meta[name="description"]')?.getAttribute('content'),
      canonical: documentResponse.querySelector('link[rel="canonical"]')?.getAttribute('href'),
      template: incomingMain.dataset.template,
      nodes: [...incomingMain.childNodes].map((node) => document.importNode(node, true)),
    };
    cache.set(key, page);
    return page;
  };

  const routeEasing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  const fadeMain = async (from, to, duration, easing) => {
    const animation = main.animate([{ opacity: from }, { opacity: to }], { duration, easing, fill: 'forwards' });
    await animation.finished.catch(() => {});
  };

  const morphNav = async (layout) => {
    const targets = slots();
    const first = new Map(targets.map((slot) => [slot, slot.getBoundingClientRect()]));
    shell.classList.toggle('is-compact', layout === 'compact');
    shell.classList.toggle('is-expanded', layout !== 'compact');
    const animations = targets.map((slot) => {
      const from = first.get(slot);
      const to = slot.getBoundingClientRect();
      if (!from?.width || !to.width) return null;
      const scaleX = from.width / to.width;
      const scaleY = from.height / to.height;
      const translateX = from.left - to.left;
      const translateY = from.top - to.top;
      // Some compact-nav slots have a deliberate resting offset. Animate to
      // that computed transform so it travels with the heart instead of
      // appearing only after the Web Animation is cancelled.
      const restingTransform = getComputedStyle(slot).transform;
      return slot.animate([
        { transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})` },
        { transform: restingTransform === 'none' ? 'translate3d(0, 0, 0) scale(1, 1)' : restingTransform },
      ], { duration: 360, easing: routeEasing, fill: 'both' });
    }).filter(Boolean);
    await Promise.allSettled(animations.map((animation) => animation.finished));
    animations.forEach((animation) => animation.cancel());
  };

  const applyHead = (page) => {
    if (page.title) document.title = page.title;
    const description = document.querySelector('meta[name="description"]');
    if (description && page.description) description.content = page.description;
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical && page.canonical) canonical.href = page.canonical;
  };

  const swap = async (page, { alreadyFadedOut = false } = {}) => {
    main.setAttribute('aria-busy', 'true');
    if (!alreadyFadedOut) await fadeMain(1, 0, 120, 'ease-out');
    current.nodes = [...main.childNodes];
    cache.set(current.key, current);
    main.replaceChildren(...page.nodes);
    if (page.template) main.dataset.template = page.template;
    // Imported scripts must run the first time a fetched page is mounted, but
    // must not run again when that page's existing DOM nodes are restored from
    // the route cache. Re-running them duplicates document/window listeners.
    if (!page.scriptsActivated) {
      activateScripts(main);
      page.scriptsActivated = true;
    }
    current = page;
    applyHead(page);
    main.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 210, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' });
    main.removeAttribute('aria-busy');
    setCartCount();
  };

  const route = async (destination, { history = 'push' } = {}) => {
    releaseLookbookViewer();
    if (!canEnhance() || !isEligible(destination)) {
      // A browser Back/Forward action has already moved the URL before it
      // emits popstate. Reassigning that identical URL recursively emits more
      // history work in some mobile browsers, eventually exhausting the call
      // stack. Only delegate to a full navigation when the destination differs.
      if (keyFor(destination) !== keyFor(new URL(window.location.href))) location.assign(destination.href);
      return;
    }
    const key = keyFor(destination);
    const browserKey = keyFor(new URL(window.location.href));
    if (key === current.key && key === browserKey) return;
    if (destination.pathname === '/' && main.querySelector('.angel-home')) {
      await morphNav('expanded');
      current.key = key;
      if (history === 'push') window.history.pushState({ angelRoute: true }, '', destination.pathname + destination.search + destination.hash);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    try {
      const page = await fetchPage(destination);
      main.setAttribute('aria-busy', 'true');
      await Promise.allSettled([
        morphNav(targetLayout(destination)),
        fadeMain(1, 0, 360, routeEasing),
      ]);
      // The incoming section reads location/hash while it initializes. Update
      // history before swapping it in so Home never boots with the departing
      // product route, which could leave its first hash navigation stale.
      if (history === 'push') window.history.pushState({ angelRoute: true }, '', destination.pathname + destination.search + destination.hash);
      await swap(page, { alreadyFadedOut: true });
      if (destination.pathname === '/') window.dispatchEvent(new HashChangeEvent('hashchange'));
      window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (error) {
      if (error.name !== 'AbortError') location.assign(destination.href);
    }
  };

  const linkFromEvent = (event) => event.target.closest('[data-angel-route-shell] a, a[data-angel-product-route], a[href^="/products/"]');
  document.addEventListener('click', (event) => {
    const link = linkFromEvent(event);
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target || link.hasAttribute('download')) return;
    const destination = new URL(link.href, location.href);
    if (!isEligible(destination)) return;
    event.preventDefault();
    route(destination);
  }, true);

  document.addEventListener('pointerover', (event) => {
    const link = linkFromEvent(event);
    if (!link || !canEnhance()) return;
    const destination = new URL(link.href, location.href);
    if (isEligible(destination) && keyFor(destination) !== current.key) fetchPage(destination).catch(() => {});
  }, { passive: true });

  window.addEventListener('popstate', () => route(new URL(location.href), { history: 'pop' }));
  shell.querySelector('[data-angel-shell-cart]')?.addEventListener('click', (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(event.currentTarget);
  });
  window.addEventListener('angel:cart-updated', (event) => applyCartCount(event.detail?.cart));
  setCartCount();
})();
